<template>
  <div class="action-page">
    <!-- 统计数据部分 -->
    <div class="stats-section">
      <div class="section-header">
        <h2>功能使用统计</h2>
        <div class="header-actions">
          <button class="refresh-btn" @click="refreshActionData" :disabled="refreshing">
            {{ refreshing ? '刷新中...' : '刷新数据' }}
          </button>
          <p class="update-time">更新时间：{{ updateTime }}</p>
        </div>
      </div>
      
      <div v-if="refreshError" class="refresh-error">{{ refreshError }}</div>
      <div v-if="loading">正在加载...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="featureData">
        <!-- 功能使用概览 -->
        <div class="stat-group">
          <h3>功能使用概览</h3>
          <div class="usage-overview">
            <div class="stat-card">
              <div class="stat-title">总功能使用数</div>
              <div class="stat-value">{{ featureData.totalActions?.toLocaleString() || 0 }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">使用用户数</div>
              <div class="stat-value">{{ featureData.uniqueUsers?.toLocaleString() || 0 }}</div>
            </div>
            <div class="stat-card" v-for="name in topFunctions.slice(0, 3)" :key="name">
              <div class="stat-title">{{ name }}</div>
              <div class="stat-value">{{ getFunctionCount(name) }}</div>
            </div>
          </div>
        </div>
        
        <!-- 功能使用趋势 -->
        <div class="stat-group">
          <h3>功能使用趋势</h3>
          <div class="charts-container">
            <div class="chart-section">
              <h4>日功能使用趋势</h4>
              <v-chart class="chart" :option="dailyUsageChartOption" :key="`daily-usage-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>周功能使用趋势</h4>
              <v-chart class="chart" :option="weeklyUsageChartOption" :key="`weekly-usage-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
        
        <!-- 功能使用分布 -->
        <div class="stat-group">
          <h3>功能使用分布</h3>
          <div class="charts-container">
            <div class="chart-section">
              <h4>功能使用比例</h4>
              <v-chart class="chart" :option="actionDistributionOption" :key="`action-dist-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>用户覆盖率</h4>
              <v-chart class="chart" :option="userCoverageOption" :key="`user-coverage-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
        
        <!-- VIP功能使用趋势 -->
        <div class="stat-group" v-if="featureData?.vipFeatures && featureData.vipFeatures.length > 0">
          <h3>VIP功能使用趋势</h3>
          <div class="charts-container">
            <div class="chart-section">
              <h4>日VIP功能使用趋势</h4>
              <v-chart class="chart" :option="vipDailyUsageChartOption" :key="`vip-daily-usage-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>周VIP功能使用趋势</h4>
              <v-chart class="chart" :option="vipWeeklyUsageChartOption" :key="`vip-weekly-usage-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
        
        <!-- VIP功能使用分布 -->
        <div class="stat-group" v-if="featureData?.vipFeatures && featureData.vipFeatures.length > 0">
          <h3>VIP功能使用分布</h3>
          <div class="charts-container">
            <div class="chart-section">
              <h4>VIP功能使用比例</h4>
              <v-chart class="chart" :option="vipActionDistributionOption" :key="`vip-action-dist-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>VIP用户覆盖率</h4>
              <v-chart class="chart" :option="vipUserCoverageOption" :key="`vip-user-coverage-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 活跃度统计部分 -->
    <div class="stats-section">
      <div class="section-header">
        <h2>活跃度统计</h2>
        <div class="header-actions">
          <button class="refresh-btn" @click="refreshActivityData" :disabled="activityRefreshing">
            {{ activityRefreshing ? '刷新中...' : '刷新数据' }}
          </button>
          <p class="update-time">更新时间：{{ activityUpdateTime }}</p>
        </div>
      </div>
      
      <div v-if="activityRefreshError" class="refresh-error">{{ activityRefreshError }}</div>
      <div v-if="activityLoading">正在加载...</div>
      <div v-else-if="activityError" class="error">{{ activityError }}</div>
      <div v-else-if="activityData">
        <!-- 用户活跃度统计 -->
        <div class="stat-group">
          <h3>用户活跃度统计</h3>
          <div class="charts-container">
            <div class="chart-section">
              <h4>日活跃用户趋势</h4>
              <v-chart class="chart" :option="actionDailyChartOption" :key="`action-daily-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>周活跃用户趋势</h4>
              <v-chart class="chart" :option="actionWeeklyChartOption" :key="`action-weekly-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>月活跃用户趋势</h4>
              <v-chart class="chart" :option="actionMonthlyChartOption" :key="`action-monthly-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
        
        <!-- 活跃度时间分布 -->
        <div class="stat-group">
          <h3>用户活跃度时间分布</h3>
          <div class="charts-container full-width">
            <div class="chart-section wide-chart">
              <h4>全天活跃度分布</h4>
              <v-chart class="chart" :option="activityTimeDistributionOption" :key="`activity-time-dist-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import VChart from 'vue-echarts'

export default {
  name: 'ActionPage',
  components: {
    VChart
  },
  data() {
    return {
      // 数据加载状态
      loading: true,
      refreshing: false,
      error: null,
      refreshError: null,
      
      // 活跃度数据加载状态
      activityLoading: true,
      activityRefreshing: false,
      activityError: null,
      activityRefreshError: null,
      
      // 统计数据
      featureData: null,  // 功能使用数据
      activityData: null,  // 活跃度数据
      updateTime: '',
      activityUpdateTime: '', // 活跃度统计的更新时间
      
      // 图表更新相关
      chartUpdateKey: 0,
      
      // 为 ResizeObserver 添加节流
      resizeObserverThrottleTimer: null
    }
  },
  computed: {
    // 获取排名前的函数名
    topFunctions() {
      if (!this.featureData?.featureCounts?.featureNames) return [];
      const funcNames = [...this.featureData.featureCounts.featureNames];
      const counts = [...this.featureData.featureCounts.counts];
      
      // 创建函数名和计数的配对，然后按计数排序
      const pairs = funcNames.map((name, index) => ({ name, count: counts[index] }));
      pairs.sort((a, b) => b.count - a.count);
      
      return pairs.map(pair => pair.name);
    },
    
    // 图表基础配置
    chartBaseOption() {
      return {
        tooltip: {
          trigger: 'axis'
        },
        grid: {
          top: 40,
          right: 20,
          bottom: 60,
          left: 60,
          containLabel: true
        },
        dataZoom: [
          {
            type: 'slider',
            show: true,
            start: 0,
            end: 100,
            bottom: 10
          },
          {
            type: 'inside',
            start: 0,
            end: 100
          }
        ],
        xAxis: {
          type: 'category',
          axisLabel: {
            rotate: 45,
            interval: 'auto'
          }
        },
        yAxis: {
          type: 'value'
        },
        noDataLoadingOption: {
          text: '暂无数据',
          textStyle: {
            fontSize: 14,
            color: '#909399'
          },
          effect: 'bubble'
        }
      }
    },
    
    // 日功能使用趋势图表选项
    dailyUsageChartOption() {
      if (!this.featureData?.daily?.dates || !this.featureData?.daily?.data) return {};
      
      const startIndex = this.filterDataFrom2025April(this.featureData.daily.dates);
      
      const dates = this.featureData.daily.dates.slice(startIndex);
      const series = [];
      
      // 显示所有功能
      const allFunctions = Object.keys(this.featureData.daily.data);
      
      // 生成每个功能的序列
      allFunctions.forEach((funcName, index) => {
        series.push({
          name: funcName,
          type: 'line',
          data: this.featureData.daily.data[funcName].slice(startIndex),
          itemStyle: {
            color: this.getColorByIndex(index)
          }
        });
      });
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates
        },
        legend: {
          type: 'scroll',
          orient: 'horizontal',
          bottom: 0,
          padding: [5, 10],
          itemGap: 20,
          textStyle: {
            fontSize: 12
          },
          pageButtonItemGap: 5,
          pageButtonGap: 5,
          pageButtonPosition: 'end',
          pageIconColor: '#909399',
          pageIconInactiveColor: '#C0C4CC',
          pageIconSize: 12,
          pageTextStyle: {
            color: '#606266'
          }
        },
        series
      };
    },
    
    // 周功能使用趋势图表选项
    weeklyUsageChartOption() {
      if (!this.featureData?.weekly?.dates || !this.featureData?.weekly?.data) return {};
      
      const startIndex = this.filterDataFrom2025April(this.featureData.weekly.dates);
      
      const dates = this.featureData.weekly.dates.slice(startIndex);
      const series = [];
      
      // 显示所有功能
      const allFunctions = Object.keys(this.featureData.weekly.data);
      
      // 生成每个功能的序列
      allFunctions.forEach((funcName, index) => {
        series.push({
          name: funcName,
          type: 'line',
          data: this.featureData.weekly.data[funcName].slice(startIndex),
          itemStyle: {
            color: this.getColorByIndex(index)
          }
        });
      });
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates
        },
        legend: {
          type: 'scroll',
          orient: 'horizontal',
          bottom: 0,
          padding: [5, 10],
          itemGap: 20,
          textStyle: {
            fontSize: 12
          },
          pageButtonItemGap: 5,
          pageButtonGap: 5,
          pageButtonPosition: 'end',
          pageIconColor: '#909399',
          pageIconInactiveColor: '#C0C4CC',
          pageIconSize: 12,
          pageTextStyle: {
            color: '#606266'
          }
        },
        series
      };
    },
    
    // 日VIP功能使用趋势图表选项
    vipDailyUsageChartOption() {
      if (!this.featureData?.daily?.dates || !this.featureData?.daily?.data || !this.featureData?.vipFeatures) return {};
      
      const startIndex = this.filterDataFrom2025April(this.featureData.daily.dates);
      
      const dates = this.featureData.daily.dates.slice(startIndex);
      const series = [];
      
      // 只显示VIP功能
      const vipFeatures = this.featureData.vipFeatures;
      const allFunctions = Object.keys(this.featureData.daily.data);
      
      // 过滤出VIP功能
      const vipFunctions = allFunctions.filter(funcName => vipFeatures.includes(funcName));
      
      // 生成每个VIP功能的序列
      vipFunctions.forEach((funcName, index) => {
        series.push({
          name: funcName,
          type: 'line',
          data: this.featureData.daily.data[funcName].slice(startIndex),
          itemStyle: {
            color: this.getColorByIndex(index)
          }
        });
      });
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates
        },
        legend: {
          type: 'scroll',
          orient: 'horizontal',
          bottom: 0,
          padding: [5, 10],
          itemGap: 20,
          textStyle: {
            fontSize: 12
          },
          pageButtonItemGap: 5,
          pageButtonGap: 5,
          pageButtonPosition: 'end',
          pageIconColor: '#909399',
          pageIconInactiveColor: '#C0C4CC',
          pageIconSize: 12,
          pageTextStyle: {
            color: '#606266'
          }
        },
        series
      };
    },
    
    // 周VIP功能使用趋势图表选项
    vipWeeklyUsageChartOption() {
      if (!this.featureData?.weekly?.dates || !this.featureData?.weekly?.data || !this.featureData?.vipFeatures) return {};
      
      const startIndex = this.filterDataFrom2025April(this.featureData.weekly.dates);
      
      const dates = this.featureData.weekly.dates.slice(startIndex);
      const series = [];
      
      // 只显示VIP功能
      const vipFeatures = this.featureData.vipFeatures;
      const allFunctions = Object.keys(this.featureData.weekly.data);
      
      // 过滤出VIP功能
      const vipFunctions = allFunctions.filter(funcName => vipFeatures.includes(funcName));
      
      // 生成每个VIP功能的序列
      vipFunctions.forEach((funcName, index) => {
        series.push({
          name: funcName,
          type: 'line',
          data: this.featureData.weekly.data[funcName].slice(startIndex),
          itemStyle: {
            color: this.getColorByIndex(index)
          }
        });
      });
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates
        },
        legend: {
          type: 'scroll',
          orient: 'horizontal',
          bottom: 0,
          padding: [5, 10],
          itemGap: 20,
          textStyle: {
            fontSize: 12
          },
          pageButtonItemGap: 5,
          pageButtonGap: 5,
          pageButtonPosition: 'end',
          pageIconColor: '#909399',
          pageIconInactiveColor: '#C0C4CC',
          pageIconSize: 12,
          pageTextStyle: {
            color: '#606266'
          }
        },
        series
      };
    },
    
    // 功能使用分布饼图选项
    actionDistributionOption() {
      if (!this.featureData?.featureCounts) return {};
      
      const data = [];
      
      if (this.featureData.featureCounts.featureNames && 
          this.featureData.featureCounts.counts && 
          this.featureData.featureCounts.percentages) {
            
        // 显示所有功能
        this.featureData.featureCounts.featureNames.forEach((name, index) => {
          data.push({
            name: name,
            value: this.featureData.featureCounts.counts[index],
            itemStyle: {
              color: this.getColorByIndex(index)
            }
          });
        });
      }
      
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 10,
          top: 20,
          bottom: 20,
          padding: [5, 10],
          itemGap: 10,
          textStyle: {
            fontSize: 12
          },
          pageButtonItemGap: 5,
          pageButtonPosition: 'end'
        },
        series: [
          {
            name: '功能使用分布',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 4,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              formatter: '{b}: {d}%'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '15',
                fontWeight: 'bold'
              }
            },
            data
          }
        ]
      };
    },
    
    // 用户覆盖率图表选项
    userCoverageOption() {
      if (!this.featureData?.userCoverage) return {};
      
      if (this.featureData.userCoverage.featureNames && 
          this.featureData.userCoverage.userCounts && 
          this.featureData.userCoverage.coveragePercentages) {
            
        // 显示所有功能
        const functionNames = [...this.featureData.userCoverage.featureNames];
        const coveragePercentages = [...this.featureData.userCoverage.coveragePercentages];
        
        // 颠倒顺序以便在图表底部显示最高的值
        functionNames.reverse();
        coveragePercentages.reverse();
        
        return {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            },
            formatter: function(params) {
              return `${params[0].name}: ${params[0].value}%`;
            }
          },
          grid: {
            left: '3%',
            right: '8%',
            bottom: '3%',
            containLabel: true
          },
          dataZoom: [
            {
              type: 'slider',
              show: true,
              yAxisIndex: [0],
              width: 15,
              right: 10,
              start: 0,
              end: 100 // 默认显示全部数据
            },
            {
              type: 'inside',
              yAxisIndex: [0],
              start: 0,
              end: 100
            }
          ],
          xAxis: {
            type: 'value',
            boundaryGap: [0, 0.01],
            axisLabel: {
              formatter: '{value}%'
            },
            max: 100
          },
          yAxis: {
            type: 'category',
            data: functionNames,
            axisLabel: {
              fontSize: 11,
              width: 120,
              overflow: 'truncate'
            }
          },
          series: [
            {
              name: '用户覆盖率',
              type: 'bar',
              data: coveragePercentages,
              label: {
                show: true,
                position: 'right',
                formatter: '{c}%'
              },
              itemStyle: {
                color: function(params) {
                  return this.getColorByIndex(functionNames.length - 1 - params.dataIndex);
                }.bind(this)
              }
            }
          ]
        };
      }
      
      return {};
    },
    
    // VIP功能使用分布饼图选项
    vipActionDistributionOption() {
      if (!this.featureData?.featureCounts || !this.featureData?.vipFeatures) return {};
      
      const data = [];
      const vipFeatures = this.featureData.vipFeatures;
      
      if (this.featureData.featureCounts.featureNames && 
          this.featureData.featureCounts.counts && 
          this.featureData.featureCounts.percentages) {
            
        // 只显示VIP功能
        this.featureData.featureCounts.featureNames.forEach((name, index) => {
          if (vipFeatures.includes(name)) {
            data.push({
              name: name,
              value: this.featureData.featureCounts.counts[index],
              itemStyle: {
                color: this.getColorByIndex(index)
              }
            });
          }
        });
      }
      
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 10,
          top: 20,
          bottom: 20,
          padding: [5, 10],
          itemGap: 10,
          textStyle: {
            fontSize: 12
          },
          pageButtonItemGap: 5,
          pageButtonPosition: 'end'
        },
        series: [
          {
            name: 'VIP功能使用分布',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 4,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              formatter: '{b}: {d}%'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '15',
                fontWeight: 'bold'
              }
            },
            data
          }
        ]
      };
    },
    
    // VIP用户覆盖率图表选项
    vipUserCoverageOption() {
      if (!this.featureData?.userCoverage || !this.featureData?.vipFeatures) return {};
      
      const vipFeatures = this.featureData.vipFeatures;
      
      if (this.featureData.userCoverage.featureNames && 
          this.featureData.userCoverage.userCounts && 
          this.featureData.userCoverage.coveragePercentages) {
            
        // 只显示VIP功能
        const functionNames = [];
        const coveragePercentages = [];
        const originalIndices = [];
        
        this.featureData.userCoverage.featureNames.forEach((name, index) => {
          if (vipFeatures.includes(name)) {
            functionNames.push(name);
            coveragePercentages.push(this.featureData.userCoverage.coveragePercentages[index]);
            originalIndices.push(index);
          }
        });
        
        // 颠倒顺序以便在图表底部显示最高的值
        functionNames.reverse();
        coveragePercentages.reverse();
        originalIndices.reverse();
        
        return {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            },
            formatter: function(params) {
              return `${params[0].name}: ${params[0].value}%`;
            }
          },
          grid: {
            left: '3%',
            right: '8%',
            bottom: '3%',
            containLabel: true
          },
          dataZoom: [
            {
              type: 'slider',
              show: true,
              yAxisIndex: [0],
              width: 15,
              right: 10,
              start: 0,
              end: 100 // 默认显示全部数据
            },
            {
              type: 'inside',
              yAxisIndex: [0],
              start: 0,
              end: 100
            }
          ],
          xAxis: {
            type: 'value',
            boundaryGap: [0, 0.01],
            axisLabel: {
              formatter: '{value}%'
            },
            max: 100
          },
          yAxis: {
            type: 'category',
            data: functionNames,
            axisLabel: {
              fontSize: 11,
              width: 120,
              overflow: 'truncate'
            }
          },
          series: [
            {
              name: 'VIP用户覆盖率',
              type: 'bar',
              data: coveragePercentages,
              label: {
                show: true,
                position: 'right',
                formatter: '{c}%'
              },
              itemStyle: {
                color: function(params) {
                  return this.getColorByIndex(originalIndices[functionNames.length - 1 - params.dataIndex]);
                }.bind(this)
              }
            }
          ]
        };
      }
      
      return {};
    },
    
    // 活跃度时间分布图表选项
    activityTimeDistributionOption() {
      if (!this.activityData?.timeDistribution?.timeSlots || !this.activityData?.timeDistribution?.counts) return {};
      
      let timeSlots = this.activityData.timeDistribution.timeSlots;
      let counts = this.activityData.timeDistribution.counts;
      
      // 找到6:00的索引位置
      const startIndex = timeSlots.findIndex(slot => slot === '06:00');
      
      // 如果找到6:00，则重新排序数组使6:00为起点
      if (startIndex !== -1) {
        // 将数组分割并重组
        timeSlots = [...timeSlots.slice(startIndex), ...timeSlots.slice(0, startIndex)];
        counts = [...counts.slice(startIndex), ...counts.slice(0, startIndex)];
      }
      
      // 为了更好地可视化，我们将时间槽转换为小时格式
      const formattedTimeSlots = timeSlots.map(slot => {
        // 例如：将"09:15"显示为"9:15"，去除前导零
        const [hour, minute] = slot.split(':');
        return `${parseInt(hour)}:${minute}`;
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          formatter: function(params) {
            const timeSlot = params[0].name;
            const count = params[0].value;
            return `${timeSlot}<br/>活跃度: ${count}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: formattedTimeSlots,
          axisLabel: {
            interval: 11,  // 每3小时显示一个标签
            rotate: 45
          },
          splitLine: {
            show: true,
            interval: function(index) {
              // 每小时（即每4个数据点）显示一条分隔线
              return index % 4 === 0;
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '活跃度'
        },
        dataZoom: [
          {
            type: 'slider',
            show: true,
            start: 0,
            end: 100,
            bottom: 10
          },
          {
            type: 'inside',
            start: 0,
            end: 100
          }
        ],
        series: [
          {
            name: '活跃度',
            type: 'line',
            data: counts,
            smooth: true,
            areaStyle: {
              opacity: 0.3
            },
            markPoint: {
              data: [
                {type: 'max', name: '最高值'},
                {type: 'min', name: '最低值'}
              ]
            }
          }
        ]
      };
    },
    
    // 下面添加用户活跃行为图表配置
    actionDailyChartOption() {
      if (!this.activityData?.daily?.dates) return {};
      
      const dates = this.activityData.daily.dates;
      const activeUsers = this.activityData.daily.activeUsers;
      const active3Days = this.activityData.daily.active3Days;
      const active10Days = this.activityData.daily.active10Days;
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates
        },
        legend: {
          data: ['活跃1天', '活跃3天', '活跃10天']
        },
        series: [
          {
            name: '活跃1天',
            type: 'line',
            data: activeUsers,
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '活跃3天',
            type: 'line',
            data: active3Days,
            itemStyle: {
              color: '#67C23A'
            }
          },
          {
            name: '活跃10天',
            type: 'line',
            data: active10Days,
            itemStyle: {
              color: '#E6A23C'
            }
          }
        ]
      };
    },
    
    actionWeeklyChartOption() {
      if (!this.activityData?.weekly?.dates) return {};
      
      const dates = this.activityData.weekly.dates;
      const activeUsers = this.activityData.weekly.activeUsers;
      const active3Days = this.activityData.weekly.active3Days;
      const active10Days = this.activityData.weekly.active10Days;
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates,
          axisLabel: {
            ...this.chartBaseOption.xAxis.axisLabel,
            formatter: function(value) {
              if (!value) return '';
              const [year, month, day] = value.split('-').map(Number);
              if (isNaN(year) || isNaN(month) || isNaN(day)) return '';
              const weekNum = Math.ceil((new Date(year, month - 1, day).getTime() - new Date(year, 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
              return `${year}年第${weekNum}周`;
            }
          }
        },
        legend: {
          data: ['活跃1天', '活跃3天', '活跃10天']
        },
        series: [
          {
            name: '活跃1天',
            type: 'line',
            data: activeUsers,
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '活跃3天',
            type: 'line',
            data: active3Days,
            itemStyle: {
              color: '#67C23A'
            }
          },
          {
            name: '活跃10天',
            type: 'line',
            data: active10Days,
            itemStyle: {
              color: '#E6A23C'
            }
          }
        ]
      };
    },
    
    actionMonthlyChartOption() {
      if (!this.activityData?.monthly?.dates) return {};
      
      const dates = this.activityData.monthly.dates;
      const activeUsers = this.activityData.monthly.activeUsers;
      const active3Days = this.activityData.monthly.active3Days;
      const active10Days = this.activityData.monthly.active10Days;
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates,
          axisLabel: {
            ...this.chartBaseOption.xAxis.axisLabel,
            formatter: function(value) {
              if (!value) return '';
              const [year, month] = value.split('-').map(Number);
              if (isNaN(year) || isNaN(month)) return '';
              return `${year}年${month}月`;
            }
          }
        },
        legend: {
          data: ['活跃1天', '活跃3天', '活跃10天']
        },
        series: [
          {
            name: '活跃1天',
            type: 'line',
            data: activeUsers,
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '活跃3天',
            type: 'line',
            data: active3Days,
            itemStyle: {
              color: '#67C23A'
            }
          },
          {
            name: '活跃10天',
            type: 'line',
            data: active10Days,
            itemStyle: {
              color: '#E6A23C'
            }
          }
        ]
      };
    }
  },
  mounted() {
    this.loadActionData();
    this.loadActionActivities();
    
    // 添加窗口大小调整事件处理，使用节流函数
    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeUnmount() {
    // 移除窗口大小调整事件处理
    window.removeEventListener('resize', this.handleWindowResize);
  },
  methods: {
    // 过滤2025年4月之后的数据
    filterDataFrom2025April(dates) {
      // 防御性检查，避免对undefined调用findIndex
      if (!dates || !Array.isArray(dates)) return 0;
      
      const startIndex = dates.findIndex(date => {
        if (!date) return false;
        const parts = date.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        
        return (year > 2025) || (year === 2025 && month >= 4);
      });
      return startIndex >= 0 ? startIndex : 0;
    },
    
    // 获取功能的使用次数
    getFunctionCount(name) {
      if (!this.featureData?.featureCounts?.featureNames) return 0;
      
      const index = this.featureData.featureCounts.featureNames.indexOf(name);
      if (index === -1) return 0;
      
      return this.featureData.featureCounts.counts[index].toLocaleString();
    },
    
    // 根据索引获取颜色
    getColorByIndex(index) {
      const colors = [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#336699', '#99CC99', '#FFCC99', '#FF9999', '#666666',
        '#0066CC', '#339933', '#FF6600', '#CC3333', '#333333'
      ];
      return colors[index % colors.length];
    },
    
    // 处理窗口大小调整的节流函数
    handleWindowResize() {
      if (this.resizeObserverThrottleTimer) {
        return;
      }
      
      this.resizeObserverThrottleTimer = setTimeout(() => {
        // 更新图表 key 以强制刷新
        this.chartUpdateKey += 1;
        this.resizeObserverThrottleTimer = null;
      }, 100); // 100ms 的节流
    },
    
    // 加载功能使用数据
    async loadActionData() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await axios.get('/api/actions/features', this.getAuthHeaders());
        
        if (response.data.code === 200) {
          this.featureData = response.data.data;
          this.updateTime = this.featureData.updateTime || new Date().toLocaleString();
        } else if (response.data.code !== 401 && response.data.code !== 403) {
          this.error = response.data.message;
        }
      } catch (error) {
        console.error('获取功能使用数据失败:', error);
        // 只显示非认证错误
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
          this.error = `请求失败: ${error.message}`;
        }
      } finally {
        this.loading = false;
      }
    },
    
    // 刷新功能使用数据
    async refreshActionData() {
      if (this.refreshing) return;
      
      this.refreshing = true;
      this.refreshError = null;
      
      try {
        const response = await axios.post('/api/actions/features/refresh', {}, this.getAuthHeaders());
        if (response.data.code === 200) {
          // 刷新成功后重新获取数据
          await this.loadActionData();
        } else {
          // 只有非授权错误才设置刷新错误
          if (response.data.code !== 401 && response.data.code !== 403) {
            this.refreshError = response.data.message || '刷新数据失败';
          }
        }
      } catch (error) {
        console.error('刷新功能使用数据失败:', error);
        // 只显示非认证错误
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
          this.refreshError = `刷新失败: ${error.message}`;
        }
        this.handleAuthError();
      } finally {
        this.refreshing = false;
      }
    },
    
    // 获取带授权头的配置
    getAuthHeaders() {
      return {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      };
    },
    
    // 处理认证错误
    handleAuthError() {
      this.$emit('auth-error');
    },
    
    // 加载活跃度数据
    async loadActionActivities() {
      this.activityLoading = true;
      this.activityError = null;
      
      try {
        const response = await axios.get('/api/actions/activities', this.getAuthHeaders());
        
        if (response.data.code === 200) {
          this.activityData = response.data.data;
          
          // 设置活跃度统计的更新时间
          if (this.activityData.updateTime) {
            this.activityUpdateTime = this.activityData.updateTime;
          }
        } else if (response.data.code !== 401 && response.data.code !== 403) {
          this.activityError = response.data.message || '获取活跃度数据失败';
        }
      } catch (error) {
        console.error('获取活跃度数据失败:', error);
        // 只显示非认证错误
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
          this.activityError = `请求失败: ${error.message}`;
        }
      } finally {
        this.activityLoading = false;
      }
    },
    
    // 刷新活跃度数据
    async refreshActivityData() {
      if (this.activityRefreshing) return;
      
      this.activityRefreshing = true;
      this.activityRefreshError = null;
      
      try {
        const response = await axios.post('/api/actions/activities/refresh', {}, this.getAuthHeaders());
        if (response.data.code === 200) {
          // 刷新成功后重新获取数据
          await this.loadActionActivities();
        } else {
          // 只有非授权错误才设置刷新错误
          if (response.data.code !== 401 && response.data.code !== 403) {
            this.activityRefreshError = response.data.message || '刷新数据失败';
          }
        }
      } catch (error) {
        console.error('刷新活跃度数据失败:', error);
        // 只显示非认证错误
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
          this.activityRefreshError = `刷新失败: ${error.message}`;
        }
        this.handleAuthError();
      } finally {
        this.activityRefreshing = false;
      }
    }
  }
}
</script>

<style scoped>
.action-page {
  padding: 25px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  max-width: 1600px;
  margin: 0 auto;
}

.stats-section {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.section-header h2 {
  font-size: 16px;
  color: #303133;
  margin: 0;
  padding: 0;
  border: none;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.update-time {
  font-size: 13px;
  color: #909399;
  margin: 0;
}

.refresh-btn {
  padding: 8px 15px;
  background-color: #409EFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.refresh-btn:hover {
  background-color: #66b1ff;
}

.refresh-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.refresh-error {
  padding: 8px 12px;
  background-color: #fef0f0;
  color: #f56c6c;
  border-radius: 4px;
  margin-bottom: 15px;
  border-left: 3px solid #f56c6c;
  text-align: left;
  font-size: 14px;
}

.error {
  padding: 20px;
  background-color: #fef0f0;
  color: #f56c6c;
  border-radius: 4px;
  text-align: center;
}

.stat-group {
  margin-bottom: 25px;
  padding: 15px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  text-align: center;
}

.stat-group:last-child {
  margin-bottom: 0;
}

.stat-group h3 {
  color: #303133;
  font-size: 16px;
  font-weight: 600;
  margin: 0 auto 20px auto;
  padding-bottom: 12px;
  border-bottom: 2px solid #409EFF;
  display: inline-block;
  position: relative;
}

.stat-group h3::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #409EFF;
  box-shadow: 0 1px 3px rgba(64, 158, 255, 0.3);
}

.stat-group h4 {
  color: #606266;
  font-size: 14px;
  margin: 0 0 10px 0;
  font-weight: 500;
  text-align: center;
}

.usage-overview {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 15px;
  padding: 8px;
}

.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  text-align: center;
  border: 1px solid #ebeef5;
  transition: all 0.3s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px 0 rgba(0,0,0,0.1);
}

.stat-title {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}

.stat-value {
  color: #409EFF;
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 6px;
}

.charts-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 0 auto;
  max-width: 95%;
}

.charts-container-stacked {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 auto;
  max-width: 95%;
}

.chart-section {
  padding: 15px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  transition: all 0.3s;
  text-align: center;
}

.chart-section:hover {
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.chart-section.wide-chart {
  grid-column: span 2;
}

.chart {
  height: 280px;
}

.wide-chart .chart {
  height: 350px;
}

/* 响应式布局调整 */
@media (max-width: 1200px) {
  .action-page {
    max-width: 1200px;
  }
  
  .usage-overview {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 960px) {
  .charts-container {
    grid-template-columns: 1fr;
  }
  
  .chart-section.wide-chart {
    grid-column: auto;
  }
  
  .chart {
    height: 240px;
  }
  
  .action-page {
    max-width: 100%;
    padding: 15px;
  }
  
  .stat-group {
    padding: 10px;
  }
}

@media (max-width: 768px) {
  .usage-overview {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    margin-top: 10px;
  }
}

@media (max-width: 480px) {
  .usage-overview {
    grid-template-columns: 1fr;
  }
  
  .action-page {
    padding: 10px;
  }
  
  .stat-group {
    padding: 8px;
  }
  
  .chart {
    height: 220px;
  }
}

.charts-container.full-width {
  grid-template-columns: 1fr;
  max-width: 100%;
}

.charts-container.full-width .chart-section {
  grid-column: 1;
  width: 100%;
}

.charts-container.full-width .chart {
  height: 400px;
}
</style> 